import { useCallback, useEffect, useState } from 'react'

import styles from './Modal.module.scss'

type ModalProps = {
    variant?: 'responsive' | 'modal' | 'bottomSheet'
    isVisiable: boolean
    onClose: () => void
    children: React.ReactNode
}

const Modal = ({ variant = 'responsive', isVisiable, onClose, children }: ModalProps) => {
    
    const [isMounted, setIsMounted] = useState<boolean>(isVisiable)
    const [isBottomSheet, setIsBottomSheet] = useState<boolean>(variant === 'bottomSheet' ? true : false)

    const updateIsBottomSheet = useCallback(() => {
        if (variant === 'responsive') {
            const isSmallScreen = window.innerWidth <= 767
            setIsBottomSheet(isSmallScreen)
        }
    }, [variant, isBottomSheet])

    useEffect(() => {
        if (isVisiable) {
            setIsMounted(true)
        } else {
            const timer = setTimeout(() => setIsMounted(false), 300)
            return () => clearTimeout(timer)
        }
    }, [isVisiable])

    useEffect(() => {
        if (!isVisiable) return

        const handleEscapeKey = (event: KeyboardEvent) => {
            if (event.key === 'Escape') onClose()
        }

        document.body.style.overflowY = 'hidden'
        document.addEventListener('keydown', handleEscapeKey)

        if (variant === 'responsive') {
            updateIsBottomSheet()
            window.addEventListener('resize', updateIsBottomSheet)
        }

        return () => {
            document.body.style.overflowY = 'auto'
            window.removeEventListener('keydown', handleEscapeKey)
            
            if (variant === 'responsive') {
                window.removeEventListener('resize', updateIsBottomSheet)
            }
        }

    }, [isVisiable, onClose, variant, updateIsBottomSheet])

    if (!isMounted) return null

    return (
        <>
            <div className={styles.overlay} onClick={onClose} data-state={isVisiable ? 'open' : 'closed'} />
            <div
                role='dialog'
                className={isBottomSheet ? styles.bottomSheetContent : `${styles.modalContent}`}
                onClick={(e) => e.stopPropagation()}
                data-state={isVisiable ? 'open' : 'closed'}
            >
                {children}
            </div>
        </>
    )
}

export default Modal